import React, { useMemo, useEffect, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { SmartCalendar } from '../../../shared/components/SmartCalendar';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { useDraggableScroll } from '../../../shared/hooks/useDraggableScroll';

interface AgendaCalendarProps {
    isGridExpanded: boolean;
    selectedCalendarDate: Date;
    setSelectedCalendarDate: (date: Date) => void;
    currentMonth: Date;
    setCurrentMonth: (date: Date) => void;
    getDateStyle?: (date: Date) => any;
}

/**
 * AgendaCalendar: Horizontal date scroller and grid calendar.
 * HARDENED: Fixed 'September 2025' default bug and improved centering safety.
 */
export const AgendaCalendar: React.FC<AgendaCalendarProps> = ({
    isGridExpanded,
    selectedCalendarDate,
    setSelectedCalendarDate,
    currentMonth,
    setCurrentMonth,
    getDateStyle
}) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const monthLabelRef = useRef<HTMLDivElement>(null);
    
    // RE-INTEGRATE DRAG PHYSICS
    const { scrollerRef: dragRef, scrollHandlers } = useDraggableScroll();

    const scrollerDates = useMemo(() => {
        const dates: Date[] = [];
        const start = new Date();
        start.setDate(start.getDate() - 180);
        for (let i = 0; i < 361; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            dates.push(d);
        }
        return dates;
    }, []);

    // HARDENED INDEX RESOLUTION
    const todayIndex = useMemo(() => {
        const todayStr = new Date().toDateString();
        const idx = scrollerDates.findIndex(d => d.toDateString() === todayStr);
        return idx !== -1 ? idx : 180;
    }, [scrollerDates]);

    const selectedIndex = useMemo(() => {
        // Safety check for selectedCalendarDate validity
        if (!selectedCalendarDate || isNaN(selectedCalendarDate.getTime())) return todayIndex;
        
        const selStr = selectedCalendarDate.toDateString();
        const idx = scrollerDates.findIndex(d => d.toDateString() === selStr);
        return idx !== -1 ? idx : todayIndex;
    }, [scrollerDates, selectedCalendarDate, todayIndex]);

    const isInitialMount = useRef(true);

    // --- REFACTORED CENTERING ENGINE ---
    useEffect(() => {
        if (!isGridExpanded && virtuosoRef.current) {
            const timer = setTimeout(() => {
                const behavior = 'auto';
                
                virtuosoRef.current?.scrollToIndex({
                    index: selectedIndex,
                    align: 'center',
                    behavior
                });
                
                if (isInitialMount.current) {
                    isInitialMount.current = false;
                }

                // Sync Month Label
                if (monthLabelRef.current) {
                    const centerDate = scrollerDates[selectedIndex];
                    if (centerDate) {
                        monthLabelRef.current.innerText = centerDate.toLocaleDateString([], { month: 'long', year: 'numeric' }).toUpperCase();
                    }
                }
            }, 100); 
            return () => clearTimeout(timer);
        }
    }, [isGridExpanded, selectedIndex, scrollerDates]);

    const renderDateItem = (index: number) => {
        const date = scrollerDates[index];
        if (!date) return null;

        const isSelected = date.toDateString() === selectedCalendarDate?.toDateString(); 
        const isToday = date.toDateString() === new Date().toDateString();
        const isFirstOfMonth = date.getDate() === 1;
        const status = getDateStyle ? getDateStyle(date) : {}; 
        
        const highlightColor = isToday ? '#2ecc71' : (status.color || '#666');
        const fontWeight = isToday ? '900' : (status.fontWeight || (isFirstOfMonth ? '900' : '800'));
        const opacity = status.opacity || 1;

        return (
            <div 
                onClick={() => setSelectedCalendarDate(date)} 
                style={{ 
                    minWidth: '44px',
                    width: '44px', 
                    height: '44px', 
                    borderRadius: '10px',
                    border: isSelected ? '2px solid #f29b11' : isFirstOfMonth ? '1px solid #444' : '1px solid #222', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    backgroundColor: isFirstOfMonth && !isSelected && !isToday ? 'rgba(255,255,255,0.02)' : 'transparent', 
                    transition: 'all 0.2s ease', 
                    position: 'relative',
                    margin: '0 3px',
                    marginTop: '12px', 
                    opacity,
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxShadow: isToday && !isSelected ? 'inset 0 0 10px rgba(46, 204, 113, 0.05)' : 'none'
                }}
            >
                {isFirstOfMonth && !isToday && (
                    <span style={{ position: 'absolute', top: '-12px', fontSize: '6px', fontWeight: '900', color: '#f29b11', letterSpacing: '0.5px' }}>{date.toLocaleDateString([], { month: 'short' }).toUpperCase()}</span>
                )}
                
                {status.markers && status.markers.length > 0 && (
                    <div style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '3px' }}>
                        {status.markers.slice(0, 4).map((m: any, i: number) => {
                            const isRec = m.type === EVENT_TYPES.RECOVERY || m.type === 'SLEEP';
                            let dotColor = '#444';
                            if (m.status === 'SKIPPED') dotColor = '#ef4444';
                            else if (m.status === 'COMPLETED' || m.status === 'ACTIVE') dotColor = isRec ? '#00f2ff' : '#2ecc71';
                            else if (m.status === 'PLANNED') dotColor = isRec ? '#3b82f6' : '#f29b11';
                            return <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: dotColor, boxShadow: m.status === 'ACTIVE' ? `0 0 4px ${dotColor}` : 'none' }} />;
                        })}
                    </div>
                )}

                <span style={{ fontSize: '8px', fontWeight: '800', textTransform: 'uppercase', color: isToday ? '#2ecc71' : isFirstOfMonth ? '#f29b11' : '#666', pointerEvents: 'none' }}>{date.toLocaleDateString([], { weekday: 'short' })}</span>
                <span style={{ fontSize: '17px', pointerEvents: 'none', fontWeight, color: highlightColor }}>{date.getDate()}</span>
            </div>
        );
    };

    return (
        <div style={{ backgroundColor: 'transparent', transition: 'all 0.3s ease', width: '100%', overflow: 'hidden' }}>
            {isGridExpanded ? (
                <div className="animate-in zoom-in-95 duration-200">
                    <SmartCalendar
                        currentMonth={currentMonth}
                        onMonthChange={setCurrentMonth}
                        selectedDate={selectedCalendarDate}
                        onSelectDate={setSelectedCalendarDate}
                        getDateStyle={getDateStyle}
                    />
                </div>
            ) : (
                <div style={{ height: '85px', width: '100%', position: 'relative' }}>
                    <div 
                        ref={monthLabelRef}
                        style={{ fontSize: '9px', fontWeight: '900', color: '#f29b11', letterSpacing: '2px', marginBottom: '4px', textTransform: 'uppercase' }}
                    >
                        {scrollerDates[selectedIndex]?.toLocaleDateString([], { month: 'long', year: 'numeric' }).toUpperCase()}
                    </div>
                    <div 
                        {...scrollHandlers}
                        style={{ height: '65px', width: '100%' }}
                    >
                        <Virtuoso
                            ref={virtuosoRef}
                            initialTopMostItemIndex={selectedIndex}
                            scrollerRef={(ref) => {
                                // @ts-ignore
                                dragRef.current = ref;
                            }}
                            horizontalDirection
                            data={scrollerDates}
                            totalCount={scrollerDates.length}
                            itemContent={(index) => renderDateItem(index)}
                            style={{ height: '100%', width: '100%' }}
                            className="no-scrollbar"
                            overscan={30}
                            rangeChanged={(range) => {
                                const centerIdx = Math.floor((range.startIndex + range.endIndex) / 2);
                                const centerDate = scrollerDates[centerIdx];
                                if (centerDate && monthLabelRef.current) {
                                    monthLabelRef.current.innerText = centerDate.toLocaleDateString([], { month: 'long', year: 'numeric' }).toUpperCase();
                                }
                            }}
                        />
                    </div>
                    <style>{`
                        .no-scrollbar::-webkit-scrollbar { display: none; }
                        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    `}</style>
                </div>
            )}
        </div>
    );
};
